import {
  validateVehicleBlueprint,
  CAR_PRESETS,
  DEFAULT_UPGRADES,
  type DummyPreset,
  type VehicleBlueprint,
} from "@neon-arena/shared";
import { Game } from "./game/Game.js";
import { AuthClient } from "./auth/AuthClient.js";
import { VehicleSelect } from "./ui/VehicleSelect.js";
import { QuickMatchScreen } from "./ui/QuickMatchScreen.js";
import { TestLab } from "./testlab/TestLab.js";
import { ComparisonLab } from "./testlab/ComparisonLab.js";
import { LabPicker } from "./ui/LabPicker.js";
import { SceneManager } from "./game/SceneManager.js";
import { InputManager } from "./input/InputManager.js";
import { ChatPanel } from "./ui/ChatPanel.js";
import { ScreenManager, type ArcadeScreen } from "./ui/ScreenManager.js";
import { MenuInput } from "./ui/MenuInput.js";
import { uiSfx } from "./ui/uiSfx.js";
import { announcer } from "./ui/announcer.js";
import { menuAnnouncer } from "./ui/announcerMenu.js";
import { initCrtOverlay, setCrtVisible, setCrtEnabled, getCrtEnabled } from "./ui/crt.js";
import { BootScreen } from "./ui/screens/BootScreen.js";
import { TitleScreen } from "./ui/screens/TitleScreen.js";
import { MainMenuScreen } from "./ui/screens/MainMenuScreen.js";
import { LobbyFlow, type LobbyLaunch } from "./ui/screens/LobbyFlow.js";
import { LobbyLandingScreen } from "./ui/screens/LobbyLandingScreen.js";
import { LobbyRoomScreen } from "./ui/screens/LobbyRoomScreen.js";
import type { VehicleSelectResult } from "./ui/VehicleSelect.js";
import { GarageScreen } from "./ui/screens/GarageScreen.js";
import { OptionsScreen } from "./ui/screens/OptionsScreen.js";
import { RankingsScreen } from "./ui/screens/RankingsScreen.js";
import { PracticeScreen } from "./ui/screens/PracticeScreen.js";
import { buildPlayerStatsHtml } from "./ui/playerStatsView.js";
import { AudioSystem } from "./audio/AudioSystem.js";
import pkg from "../package.json" with { type: "json" };

import balanced from "../../content/vehicles/balanced-starter.json" with { type: "json" };
import scout from "../../content/vehicles/glass-cannon-scout.json" with { type: "json" };
import bruiser from "../../content/vehicles/heavy-bruiser.json" with { type: "json" };
import striker from "../../content/vehicles/long-range-striker.json" with { type: "json" };
import drifter from "../../content/vehicles/drift-interceptor.json" with { type: "json" };

const ALL: unknown[] = [balanced, scout, bruiser, striker, drifter];

const blueprintLookup = new Map<string, VehicleBlueprint>();
for (const raw of ALL) {
  const result = validateVehicleBlueprint(raw);
  if (result.ok) blueprintLookup.set(result.blueprint.id, result.blueprint);
}

// ?server=wss://…  — override hardcoded server URL (dev / LAN)
// ?dummy=circle|follow|combat|parked|rammer — select bot preset (?dummy=1 also works → circle)
// ?difficulty=easy|normal|hard            — bot difficulty (default: normal)
// ?waypointThrottle=0.5                   — throttle cap for the circle preset (0–1)
const _searchParams   = new URLSearchParams(window.location.search);

const _serverOverride = _searchParams.get("server");
const SERVER_URL = _serverOverride ?? (
  location.hostname === "localhost" || location.hostname === "127.0.0.1"
    ? "ws://localhost:2567"
    : "wss://bangbop.solvx.uk"
);
const _dummyParam     = _searchParams.get("dummy");
const urlForcesDummy  = _dummyParam !== null && _dummyParam !== "0";
const urlDummyPreset: DummyPreset =
  _dummyParam === "follow"  ? "follow-player"  :
  _dummyParam === "combat"  ? "combat-follow"  :
  _dummyParam === "parked"  ? "parked"          :
  _dummyParam === "rammer"  ? "rammer"          :
                              "circle";
const _throttleParam    = _searchParams.get("waypointThrottle");
const urlWaypointThrottle: number | undefined =
  _throttleParam !== null ? Math.max(0, Math.min(1, parseFloat(_throttleParam))) : undefined;
const urlDummyDifficulty = _searchParams.get("difficulty") ?? undefined;

// ?gamepad=N — which connected gamepad this window claims (0-indexed).
// Open two browser windows to the same URL, add ?gamepad=1 to the second.
const _gamepadParam = _searchParams.get("gamepad");
const urlGamepadIndex = _gamepadParam !== null ? Math.max(0, parseInt(_gamepadParam, 10) || 0) : 0;

// ?drone=1 — observer/spectator mode: free-flying drone camera, no driving.
const urlDroneMode = _searchParams.get("drone") === "1";

// ?ui=classic — escape hatch: run the ENTIRE legacy flow (intro auto-advance →
// 6-tab screen) exactly as before; the arcade shell never mounts. Without it:
// BOOT → TITLE → MAIN MENU (docs/menu-redesign-plan.md §4.4a).
const useClassicUi = _searchParams.get("ui") === "classic";

const APP_VERSION: string = (pkg as { version?: string }).version ?? "0.0.0";

function wsToHttpUrl(url: string): string {
  return url.replace(/^wss?:\/\//, (m) => (m === "wss://" ? "https://" : "http://"));
}

const canvas = document.getElementById("game") as HTMLCanvasElement;
const errorEl = document.getElementById("start-error") as HTMLDivElement;

// Populate the hidden server input so VehicleSelect.confirm() reads the right URL
(document.getElementById("server-input") as HTMLInputElement).value = SERVER_URL;

// ── Auth ────────────────────────────────────────────────────────────────────
const auth = new AuthClient();
setupAccountTab(auth);

function showStartError(msg: string): void {
  errorEl.textContent = msg;
  errorEl.classList.remove("hidden");
  errorEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
}
function clearStartError(): void {
  errorEl.textContent = "";
  errorEl.classList.add("hidden");
}

const lookup = (id: string): VehicleBlueprint =>
  blueprintLookup.get(id) ?? blueprintLookup.values().next().value!;

const game = new Game(canvas, lookup, urlGamepadIndex);
for (const bp of blueprintLookup.values()) game.registerBlueprint(bp);

// ── Shell integration points ────────────────────────────────────────────────
// Defaults keep the ?ui=classic flow byte-for-byte identical to today;
// bootArcadeShell() rebinds them so match entry/exit lands on the new screens.
// (They are `let` + rebent rather than branched inline so the callbacks below
// stay readable and the classic path stays provably untouched.)
let onMatchEnter: () => void = () => {};
let enterOverlay: () => void = () => {};
let reshowLobby: (tab?: string) => void = () => { select.show(); };
let returnFromQuickMatch: () => void = () => { select.show(); };
let quickMatchErrorNotice: (msg: string) => void = () => {};
let startFailureNotice: (msg: string) => void = () => {};
// When set, the next confirmQuickMatch() result is captured for the hosted-lobby
// flow instead of starting a queue session (see LobbyFlow.requestLaunch wiring).
let lobbyLaunchIntercept: ((r: VehicleSelectResult) => void) | null = null;

const select = new VehicleSelect({
  auth,
  // QUICK MATCH — the always-on queue. Deferred spawn: QuickMatchScreen connects
  // and waits; `game.start(...)` only happens once the server seats us and starts
  // a round. See client/src/ui/QuickMatchScreen.ts.
  onQuickMatch: (result) => {
    if (lobbyLaunchIntercept) { const fn = lobbyLaunchIntercept; lobbyLaunchIntercept = null; fn(result); return; }
    const { driverName, serverUrl, blueprint, controlMethod, upgrades, weaponUpgrades, renderMode } = result;
    clearStartError();
    onMatchEnter();
    void quickMatch.start({
      serverUrl,
      driverName,
      blueprint,
      upgrades,
      weaponUpgrades,
      controlMethod,
      renderMode,
      authToken: auth.token,
    });
  },
  onConfirm: ({ driverName, serverUrl, blueprint, keyboardOnly, controlMethod, upgrades, weaponUpgrades, dummyOpponent, gameMode, preferredTeam, renderMode }) => {
    clearStartError();
    onMatchEnter();
    const joinBtn = document.getElementById("join-btn") as HTMLButtonElement;
    const dummyBtn = document.getElementById("dummy-btn") as HTMLButtonElement;
    joinBtn.disabled = true;
    dummyBtn.disabled = true;
    joinBtn.textContent = "CONNECTING…";

    game.setRenderMode(renderMode);
    void game.start({
      serverUrl,
      driverName,
      blueprint,
      keyboardOnly,
      controlMethod,
      upgrades,
      weaponUpgrades,
      dummyOpponent: dummyOpponent || urlForcesDummy,
      dummyPreset: urlDummyPreset,
      waypointThrottle: urlWaypointThrottle,
      dummyDifficulty: urlDummyDifficulty,
      gameMode,
      preferredTeam,
      droneMode: urlDroneMode,
      authToken: auth.token,
    }).then(() => {
      const chat = new ChatPanel(game.getNetwork());
      chat.show();
      const resetWrap = document.getElementById("reset-btn-wrap")!;
      resetWrap.classList.remove("hidden");
      document.getElementById("reset-btn")!.addEventListener("click", () => {
        game.getNetwork().sendRoundReset();
      });

      const leaveBtn = document.getElementById("leave-btn")!;
      leaveBtn.classList.remove("hidden");
      leaveBtn.addEventListener("click", () => {
        game.destroy();
        window.location.reload();
      });
    }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[neon-arena] start failed:", err);
      // Wrap cleanup so a throw inside doesn't prevent the lobby from showing
      try { game.resetForRetry(); } catch (e) { console.error("[neon-arena] resetForRetry failed:", e); }
      showStartError(`CONNECT FAILED — ${msg}`);
      startFailureNotice(`CONNECT FAILED — ${msg}`); // shell: PRACTICE screen notice strip
      // Reset button state BEFORE showing lobby — select.show() also does this
      // defensively, but belt-and-suspenders here in case something above threw
      joinBtn.disabled = false;
      dummyBtn.disabled = false;
      joinBtn.textContent = "DROP IN";
      reshowLobby("play"); // classic: select.show(); shell: back onto PRACTICE
    });
  },
});

// ── Quick match (queue room) ────────────────────────────────────────────────
// Declared after `select` because it needs to hand the lobby back on exit; used
// by `select`'s onQuickMatch callback, which only ever fires after a click.
const quickMatch = new QuickMatchScreen({
  game,
  onExit: () => { returnFromQuickMatch(); },
  onError: (msg) => { showStartError(msg); quickMatchErrorNotice(msg); },
});

document.getElementById("quickmatch-btn")!.addEventListener("click", () => {
  select.confirmQuickMatch();
});

// ── Lobby flow: intro → lobby (auth is inline, not a gate) ──────────────────
const introScreen = document.getElementById("intro-screen") as HTMLDivElement;

function updateAuthStrip(): void {
  const guestEl  = document.getElementById("auth-strip-guest");
  const userEl   = document.getElementById("auth-strip-user");
  const nameEl   = document.getElementById("auth-strip-name");
  if (!guestEl || !userEl) return;
  if (auth.isLoggedIn && auth.username) {
    guestEl.classList.add("hidden");
    userEl.classList.remove("hidden");
    if (nameEl) nameEl.textContent = auth.username;
  } else {
    userEl.classList.add("hidden");
    guestEl.classList.remove("hidden");
  }
}

function advanceToSelect(): void {
  const nameInput = document.getElementById("name-input") as HTMLInputElement;
  nameInput.value = (auth.isLoggedIn && auth.username) ? auth.username : "Driver";
  updateAuthStrip();
  select.show();
  select.switchTab("vehicle");
}

function advanceToLobby(): void {
  introScreen.classList.add("flow-exit");
  setTimeout(() => {
    introScreen.classList.add("hidden");
    introScreen.classList.remove("flow-exit");
    advanceToSelect();
  }, 320);
}

// Attempt fullscreen — must be called inside a synchronous user-gesture handler.
// Chrome/Firefox Android: hides address bar. Safari iOS: silently ignored.
function tryFullscreen(): void {
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  const req = el.requestFullscreen ?? el.webkitRequestFullscreen;
  if (req) req.call(el, { navigationUI: "hide" } as FullscreenOptions).catch(() => {/* denied or unsupported */});
}

if (useClassicUi) {
  // Legacy flow, untouched: intro auto-advance after 2 s or on any interaction
  let introTimer: ReturnType<typeof setTimeout> | null = setTimeout(advanceToLobby, 2000);
  const onIntroInteract = (): void => {
    if (introTimer !== null) { clearTimeout(introTimer); introTimer = null; }
    tryFullscreen();
    advanceToLobby();
  };
  introScreen.addEventListener("click", onIntroInteract, { once: true });
  window.addEventListener("keydown", onIntroInteract, { once: true });
} else {
  bootArcadeShell();
}

// ── Auth strip wiring ────────────────────────────────────────────────────────
(function wireAuthStrip(): void {
  document.getElementById("auth-strip-login-btn")?.addEventListener("click", () => {
    select.switchTab("account");
  });
  document.getElementById("auth-strip-register-btn")?.addEventListener("click", () => {
    select.switchTab("account");
  });
  document.getElementById("auth-strip-logout-btn")?.addEventListener("click", async () => {
    await auth.logout(SERVER_URL);
    updateAuthStrip();
    const nameInput = document.getElementById("name-input") as HTMLInputElement;
    nameInput.value = "Driver";
  });
})();

// Patch account tab login/register to also update the auth strip
(function patchAccountTabAuth(): void {
  const pane = document.querySelector<HTMLElement>('.tab-pane[data-tab="account"]');
  if (!pane) return;
  const origLogin = pane.querySelector("#acct-login-btn");
  const origReg   = pane.querySelector("#acct-reg-btn");
  origLogin?.addEventListener("click", () => setTimeout(updateAuthStrip, 300));
  origReg?.addEventListener("click",   () => setTimeout(updateAuthStrip, 300));
  pane.querySelector("#acct-logout-btn")?.addEventListener("click", () => setTimeout(updateAuthStrip, 300));
})();

document.getElementById("dummy-btn")!.addEventListener("click", () => {
  select.confirmDummy();
});


// ── Test Lab infrastructure ────────────────────────────────────
const testlabScreen = document.getElementById("testlab-screen") as HTMLDivElement;
const labPickerEl = document.getElementById("lab-picker-overlay") as HTMLDivElement;
const comparisonEl = document.getElementById("comparison-overlay") as HTMLDivElement;

// Shared scene — one renderer for the canvas, reused across all lab modes
const labScene = new SceneManager(canvas);

// Input manager for lab modes — keyboard and controller both work
const labInput = new InputManager();

const labPicker = new LabPicker(labPickerEl, {
  onStartDemo: (blueprints, presets) => {
    const upgrades = presets.map((p) => ({ ...(CAR_PRESETS[p] ?? DEFAULT_UPGRADES) }));
    const lab = new TestLab(labScene, blueprints, upgrades, () => {
      testlabScreen.classList.add("hidden");
      reshowLobby("play");
    });
    lab.start();
  },
  onStartComparison: (bpA, upgradesA, bpB, upgradesB, arena) => {
    comparisonEl.classList.remove("hidden");
    const lab = new ComparisonLab(
      labScene, bpA, upgradesA, bpB, upgradesB, arena,
      () => labInput.read(),
      comparisonEl,
      () => {
        comparisonEl.classList.add("hidden");
        testlabScreen.classList.add("hidden");
        reshowLobby("play");
      },
    );
    lab.start();
  },
  onExit: () => {
    testlabScreen.classList.add("hidden");
    reshowLobby("play");
  },
});
labPicker.setBlueprints([...blueprintLookup.values()]);

/**
 * Open the lab picker — the ONE path into TEST LAB / COMPARISON LAB, used by
 * the classic footer button and the PRACTICE screen alike (M3).
 * `focusComparison` pre-focuses the comparison side's start button.
 */
function openTestLab(focusComparison = false): void {
  enterOverlay(); // shell: hide menu screens + pause menu input (classic: no-op)
  select.hide();
  testlabScreen.classList.remove("hidden");
  labPicker.show();
  if (focusComparison) {
    (document.getElementById("lp-comp-start") as HTMLButtonElement | null)?.focus();
  }
}

document.getElementById("testlab-btn")!.addEventListener("click", () => {
  openTestLab();
});

// ── Account tab logic ───────────────────────────────────────────────────────
function setupAccountTab(authClient: AuthClient): void {
  const pane = document.querySelector<HTMLElement>('.tab-pane[data-tab="account"]');
  if (!pane) return;

  const serverInput = document.getElementById("server-input") as HTMLInputElement;

  const guestView   = pane.querySelector<HTMLElement>("#acct-guest")!;
  const loggedView  = pane.querySelector<HTMLElement>("#acct-logged")!;
  const acctName    = pane.querySelector<HTMLElement>("#acct-username")!;
  const acctErr     = pane.querySelector<HTMLElement>("#acct-error")!;
  const statsPanel  = pane.querySelector<HTMLElement>("#acct-stats")!;
  const loginForm   = pane.querySelector<HTMLElement>("#acct-login-form")!;
  const regForm     = pane.querySelector<HTMLElement>("#acct-reg-form")!;

  const render = (): void => {
    if (authClient.isLoggedIn) {
      guestView.style.display = "none";
      loggedView.style.display = "";
      acctName.textContent = authClient.username ?? "";
    } else {
      guestView.style.display = "";
      loggedView.style.display = "none";
      statsPanel.innerHTML = "";
    }
    acctErr.textContent = "";
  };

  const showErr = (msg: string): void => { acctErr.textContent = msg; };

  // Toggle between login / register sub-forms
  pane.querySelector("#acct-show-reg")?.addEventListener("click", () => {
    loginForm.style.display = "none";
    regForm.style.display = "";
    acctErr.textContent = "";
  });
  pane.querySelector("#acct-show-login")?.addEventListener("click", () => {
    regForm.style.display = "none";
    loginForm.style.display = "";
    acctErr.textContent = "";
  });

  // Input lookups use document.getElementById (unique ids), NOT pane.querySelector:
  // the OPTIONS screen ADOPTS the account form out of this pane (M3 reparenting),
  // so at click time these fields are no longer descendants of `pane`. Reading
  // them via `pane` returned null and threw before login() ran — the SIGN IN /
  // REGISTER buttons looked dead. getElementById finds them wherever they live.
  const byId = (id: string) => document.getElementById(id) as HTMLInputElement | null;

  // Login
  document.getElementById("acct-login-btn")?.addEventListener("click", async () => {
    const u = byId("acct-login-user")?.value.trim() ?? "";
    const p = byId("acct-login-pass")?.value ?? "";
    try { await authClient.login(serverInput.value, u, p); render(); }
    catch (e) { showErr(e instanceof Error ? e.message : "Login failed"); }
  });

  // Register
  document.getElementById("acct-reg-btn")?.addEventListener("click", async () => {
    const u = byId("acct-reg-user")?.value.trim() ?? "";
    const p = byId("acct-reg-pass")?.value ?? "";
    const p2 = byId("acct-reg-pass2")?.value ?? "";
    if (p !== p2) { showErr("Passwords do not match"); return; }
    try { await authClient.register(serverInput.value, u, p); render(); }
    catch (e) { showErr(e instanceof Error ? e.message : "Registration failed"); }
  });

  // Logout
  pane.querySelector("#acct-logout-btn")?.addEventListener("click", async () => {
    await authClient.logout(serverInput.value);
    render();
  });

  // Load stats — markup/math shared with the RANKINGS screen (playerStatsView, M3)
  pane.querySelector("#acct-stats-btn")?.addEventListener("click", async () => {
    statsPanel.textContent = "Loading…";
    try {
      const stats = await authClient.fetchStats(serverInput.value);
      statsPanel.innerHTML = buildPlayerStatsHtml(stats);
    } catch (e) {
      statsPanel.textContent = e instanceof Error ? e.message : "Failed to load";
    }
  });

  render();
}

// ── Arcade shell (M1) ───────────────────────────────────────────────────────
// BOOT → TITLE → MAIN MENU, with every menu entry bridging onto the existing
// screens (docs/menu-redesign-plan.md §3.1–3.3, §4). Hoisted function — called
// from the boot-flow branch above; all captured bindings exist by then.
function bootArcadeShell(): void {
  // The legacy intro never shows in shell mode.
  introScreen.classList.add("hidden");
  // VehicleSelect's Enter-to-confirm guard checks the INLINE style.display,
  // which is unset until the first show()/hide() call — force it hidden now so
  // Enter on BOOT/TITLE can never trigger a DROP IN.
  select.hide();
  initCrtOverlay();

  const host = document.getElementById("arcade-root");
  if (!host) {
    console.error("[shell] #arcade-root missing — falling back to classic flow");
    advanceToLobby();
    return;
  }

  // Announcer menu lines ride the screen router: title_intro / welcome are
  // once-per-session (menuAnnouncer gates), menu_* fire on every confirm-enter
  // (the announcer's own 1.5 s cooldown stops back-and-forth chatter), and the
  // TITLE screen arms a one-shot 8 s idle timer for press_start.
  let titleIdleTimer: ReturnType<typeof setTimeout> | null = null;
  const screens = new ScreenManager({
    host,
    onActiveChange: (name) => {
      if (titleIdleTimer !== null) { clearTimeout(titleIdleTimer); titleIdleTimer = null; }
      for (const line of menuAnnouncer.screenEntered(name)) announcer.announce(line);
      if (name === "title") {
        titleIdleTimer = setTimeout(() => {
          titleIdleTimer = null;
          for (const line of menuAnnouncer.pressStartIdle()) announcer.announce(line);
        }, 8000);
      }
    },
  });
  const menuInput = new MenuInput((action) => screens.dispatch(action));

  /** Esc/Ⓑ priority: a floating AI-builder modal closes before screens change. */
  const aiModalOpen = (): boolean => {
    const aiModal = document.getElementById("ai-builder-modal");
    return aiModal !== null && !aiModal.classList.contains("hidden");
  };
  const closeAiModalIfOpen = (): boolean => {
    if (!aiModalOpen()) return false;
    (document.getElementById("ai-modal-close") as HTMLButtonElement | null)?.click();
    return true;
  };

  // Callsign: auth username wins; guests get their last-used name back.
  const CALLSIGN_KEY = "neon-arena.callsign";
  const nameInput = document.getElementById("name-input") as HTMLInputElement;
  try {
    const saved = localStorage.getItem(CALLSIGN_KEY);
    if (saved && !auth.isLoggedIn) nameInput.value = saved.slice(0, 16);
  } catch { /* storage unavailable */ }
  if (auth.isLoggedIn && auth.username) nameInput.value = auth.username;
  nameInput.addEventListener("input", () => {
    try { localStorage.setItem(CALLSIGN_KEY, nameInput.value); } catch { /* ignore */ }
  });
  const getCallsign = (): string =>
    auth.isLoggedIn && auth.username ? auth.username : (nameInput.value || "Driver").slice(0, 16);

  // Classic bridge: the whole 6-tab screen behind one ScreenManager entry.
  // Its root is a transparent passthrough — the real UI is #start-screen.
  const backBtn = document.getElementById("lobby-back-btn");
  backBtn?.addEventListener("click", () => {
    uiSfx.play("back");
    screens.show("main-menu");
  });
  const classicScreen: ArcadeScreen = {
    mount(root) {
      root.classList.add("ui-screen--passthrough");
    },
    onEnter(params) {
      const tab = (params as { tab?: string } | undefined)?.tab ?? "play";
      updateAuthStrip();
      select.show();
      select.switchTab(tab);
      backBtn?.classList.remove("hidden");
    },
    onLeave() {
      backBtn?.classList.add("hidden");
      // Park on the PLAY tab: stops the vehicle-showcase + gamepad-test rAF
      // loops so the menus behind never pay for a hidden 3D preview. Every
      // entry into this screen passes an explicit tab, so nothing is lost.
      select.switchTab("play");
      select.hide();
    },
    handleInput(action) {
      if (action.type === "back") {
        // If the AI builder modal is open, Escape/B closes IT (via its own
        // close button) instead of dropping to the menu underneath it.
        uiSfx.play("back");
        if (closeAiModalIfOpen()) return true;
        screens.show("main-menu");
        return true;
      }
      return false; // everything else stays native (Enter = DROP IN, inputs, tabs)
    },
  };

  const mainMenu = new MainMenuScreen({
    version: APP_VERSION,
    getAuth: () => ({ loggedIn: auth.isLoggedIn, username: auth.username, credits: auth.getCredits() }),
    getCallsign,
    getVehicle: () => {
      const bp = select.selectedBlueprint();
      return bp ? { name: bp.name, id: bp.id, trim: bp.style.trimColor } : null;
    },
    refreshStats: async () => {
      if (!auth.isLoggedIn) return null;
      try {
        await auth.fetchMe(SERVER_URL);
        const stats = await auth.fetchStats(SERVER_URL);
        return {
          credits: auth.getCredits(),
          wins: stats.wins,
          losses: Math.max(0, stats.matches - stats.wins),
        };
      } catch {
        return null;
      }
    },
    onSelect: (id) => {
      if (id === "quickmatch") {
        // Same path as the classic QUICK MATCH button: reuse the lobby's
        // current loadout, hand off to QuickMatchScreen. onMatchEnter() (via
        // onQuickMatch) hides the shell before the queue overlay appears.
        // Quickmatch is an overlay, not a ScreenManager screen — announce the
        // confirm here (the router's onActiveChange never sees it).
        for (const line of menuAnnouncer.quickmatchChosen()) announcer.announce(line);
        select.confirmQuickMatch();
        return;
      }
      if (id === "lobbies") {
        // M4: hosted lobbies — host a room / join by 5-char code.
        screens.show("lobby-landing");
        return;
      }
      // M3: GARAGE / PRACTICE / RANKINGS / OPTIONS are first-class screens —
      // entry ids double as screen names. The classic bridge stays registered
      // (and ?ui=classic untouched) but nothing routes to it from here.
      screens.show(id);
    },
    onSignIn: () => screens.show("options", { section: "account" }),
    onBack: () => screens.show("title"),
  });

  // ── M3 screens: re-housed GARAGE / OPTIONS / RANKINGS / PRACTICE ─────────
  // All business logic stays in VehicleSelect / AuthClient / main.ts — the
  // screens adopt the existing DOM and drive it through narrow facades.
  const garage = new GarageScreen({
    select,
    isGuest: () => !auth.isLoggedIn,
    onSignIn: () => screens.show("options", { section: "account" }),
    onBack: () => screens.show("main-menu"),
    overlayOpen: aiModalOpen,
    closeOverlay: closeAiModalIfOpen,
  });

  // Menu-side settings handle for game SFX: same storage keys the in-game
  // AudioSystem reads at construction (changes persist; live match instance
  // is created at page load, so they apply from the next session).
  const menuGameAudio = new AudioSystem();
  const options = new OptionsScreen({
    select,
    gameAudio: {
      getVolume: () => menuGameAudio.volume,
      setVolume: (v) => menuGameAudio.setVolume(v),
      getMuted: () => menuGameAudio.muted,
      setMuted: (m) => menuGameAudio.setMuted(m),
    },
    crt: { get: getCrtEnabled, set: setCrtEnabled },
    reducedMotion: () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    onBack: () => screens.show("main-menu"),
  });

  const rankings = new RankingsScreen({
    fetchLeaderboard: () => auth.fetchLeaderboard(SERVER_URL),
    fetchStats: () => auth.fetchStats(SERVER_URL),
    isLoggedIn: () => auth.isLoggedIn,
    username: () => auth.username,
    onSignIn: () => screens.show("options", { section: "account" }),
    onBack: () => screens.show("main-menu"),
  });

  const practice = new PracticeScreen({
    onDummy: () => select.confirmDummy(), // identical to the classic DUMMY MATCH button
    onTestLab: () => openTestLab(false), // identical to the classic TEST LAB button
    onComparison: () => openTestLab(true),
    onBack: () => screens.show("main-menu"),
  });

  // Guests pressing sign-in-gated controls (AI builder, upgrade prompts) land
  // on OPTIONS → ACCOUNT instead of the classic account tab.
  select.setRequireSignIn(() => screens.show("options", { section: "account" }));

  // ── M4: hosted lobby flow (host / join-by-code / roster / ready-up) ──────
  // LobbyFlow owns the hosted-room session; the two screens are thin views.
  // Loadout comes from the same confirmQuickMatch() path as quickmatch, via a
  // one-shot intercept (see onQuickMatch above). backToShell/onMatchEnter are
  // rebound later in this function; arrow closures defer the lookup.
  const lobbyFlow = new LobbyFlow({
    game,
    requestLaunch: (): LobbyLaunch | null => {
      let captured: LobbyLaunch | null = null;
      lobbyLaunchIntercept = (r) => {
        captured = {
          serverUrl: r.serverUrl, driverName: r.driverName, blueprint: r.blueprint,
          upgrades: r.upgrades, weaponUpgrades: r.weaponUpgrades,
          controlMethod: r.controlMethod, renderMode: r.renderMode, authToken: auth.token,
        };
      };
      select.confirmQuickMatch(); // synchronous — fires the intercept above
      lobbyLaunchIntercept = null;
      return captured;
    },
    showRoom: () => screens.show("lobby-room"),
    showLanding: () => screens.show("lobby-landing"),
    hideShellForMatch: () => onMatchEnter(),
    showShellOverMatch: () => backToShell("lobby-room"),
    exitToMenu: () => backToShell("main-menu"),
    notify: (msg) => mainMenu.setNotice(msg),
  });
  screens.register("lobby-landing", new LobbyLandingScreen({ flow: lobbyFlow, onBack: () => screens.show("main-menu") }));
  screens.register("lobby-room", new LobbyRoomScreen({ flow: lobbyFlow }));

  screens.register("boot", new BootScreen({
    version: APP_VERSION,
    healthUrl: `${wsToHttpUrl(SERVER_URL)}/health`,
    onDone: () => screens.show("title"),
  }));
  screens.register("title", new TitleScreen({
    version: APP_VERSION,
    getAuthLine: () =>
      auth.isLoggedIn && auth.username ? `SIGNED IN — ${auth.username.toUpperCase()}` : "GUEST MODE",
    onStart: () => {
      uiSfx.unlock();
      uiSfx.play("confirm");
      // TITLE shows before any gesture, so its title_intro attempt is usually
      // autoplay-blocked (and the failed play frees the line again). This IS
      // the first gesture — warm the pack and replay the intro if nothing has
      // audibly played yet.
      announcer.unlock();
      if (!announcer.hasPlayed && menuAnnouncer.titleIntroEmitted) {
        announcer.announce("title_intro");
      }
      tryFullscreen(); // sync inside the gesture for keyboard/click; pads no-op
      screens.show("main-menu");
    },
  }));
  screens.register("main-menu", mainMenu);
  screens.register("classic", classicScreen);
  screens.register("garage", garage);
  screens.register("options", options);
  screens.register("rankings", rankings);
  screens.register("practice", practice);

  // ── In-game handoff ───────────────────────────────────────────────────────
  const RETURN_KEY = "neon-arena.return-to";
  const leaveShellForOverlay = (): void => {
    menuInput.stop();
    setCrtVisible(false); // no scanlines over live gameplay
    screens.hideAll();
  };
  const backToShell = (name: string, params?: unknown): void => {
    try { sessionStorage.removeItem(RETURN_KEY); } catch { /* ignore */ }
    setCrtVisible(true);
    menuInput.start();
    screens.show(name, params);
  };

  enterOverlay = leaveShellForOverlay; // test lab
  onMatchEnter = () => {
    // A reload while (or after) playing lands back on MAIN MENU, not TITLE —
    // this also covers the in-game LEAVE buttons, which exit via reload.
    try { sessionStorage.setItem(RETURN_KEY, "menu"); } catch { /* ignore */ }
    leaveShellForOverlay();
  };
  // M3: dummy-match failures and test-lab exits land back on PRACTICE (the
  // only shell path that launches them); ?ui=classic keeps the select.show()
  // default from the top of the file.
  reshowLobby = () => backToShell("practice");
  returnFromQuickMatch = () => backToShell("main-menu");
  quickMatchErrorNotice = (msg) => mainMenu.setNotice(msg);
  startFailureNotice = (msg) => practice.setNotice(msg);

  menuInput.start();

  let returnTo: string | null = null;
  try {
    returnTo = sessionStorage.getItem(RETURN_KEY);
    if (returnTo !== null) sessionStorage.removeItem(RETURN_KEY);
  } catch { /* ignore */ }
  screens.show(returnTo === "menu" ? "main-menu" : "boot");
}
